import { AddonServiceProduct } from 'models/AddonServiceProduct';
import React from 'react';
import { hasElements, rm } from 'utils';
import {
  InstallationAddon,
  TooltipProps
} from 'views/ProductPage/configurators/installation/themes/BGH/installationAddon';
import { isValidInstallation } from 'views/ProductPage/addonServices/utils';
import styles from './styles.scss';

interface AddonBaseProps {
  onClickCallback: (productId: string, selected: boolean, isCheckbox: boolean) => void;
}

interface CreateInstallationAddonProps extends AddonBaseProps {
  addonService: AddonServiceProduct;
  isCheckbox: boolean;
}

const createInstallationAddon = ({
  addonService,
  onClickCallback,
  isCheckbox
}: CreateInstallationAddonProps): JSX.Element => {
  const name = 'addonServiceItem';
  const { price, productId, title, selected, shortDescription } = addonService;
  const tooltip: TooltipProps = {
    type: 'dialog',
    description: { content: shortDescription, tag: 'div' }
  };

  return (
    <InstallationAddon
      key={productId}
      price={price}
      value={productId}
      title={title}
      name={name}
      isSelected={selected}
      onClick={() => onClickCallback(productId, selected, isCheckbox)}
      tooltip={shortDescription !== '' ? tooltip : undefined}
      isCheckbox={isCheckbox}
    />
  );
};

interface ShowChildItemsProps extends AddonBaseProps {
  addonService: AddonServiceProduct;
  validInstallations: string[];
}

const showChildItems = ({
  addonService,
  onClickCallback,
  validInstallations
}: ShowChildItemsProps): JSX.Element[] => {
  const res: JSX.Element[] = [];

  if (!isValidInstallation(addonService, validInstallations)) {
    return res;
  }

  res.push(createInstallationAddon({ addonService, onClickCallback, isCheckbox: true }));

  if (hasElements(addonService.addonServices) && addonService.selected) {
    addonService.addonServices.forEach(addon => {
      res.push(
        ...showChildItems({
          addonService: addon,
          onClickCallback,
          validInstallations
        })
      );
    });
  }

  return res;
};

interface ShowItemsProps extends AddonBaseProps {
  addonServices: AddonServiceProduct[];
  validInstallations: string[];
}

/**
 * Shows all of the valid addons.
 * If an addon is selected and has additional addon services these "child" addons will also be shown.
 *
 * @returns List of all items
 */
export const showItemSection = ({
  addonServices,
  onClickCallback,
  validInstallations
}: ShowItemsProps): JSX.Element[] => {
  const res: JSX.Element[] = [];
  const filteredAddonServices = addonServices.filter(addonService =>
    isValidInstallation(addonService, validInstallations)
  );
  //Radio btns, so only 1 can be selected
  const selectedAddon = filteredAddonServices.find(addon => addon.selected);

  res.push(
    ...filteredAddonServices.map(serviceProduct => {
      return createInstallationAddon({
        addonService: serviceProduct,
        onClickCallback,
        isCheckbox: false
      });
    })
  );

  if (selectedAddon === undefined || !hasElements(selectedAddon.addonServices)) {
    // No child addons to show
    return res;
  }

  res.push(
    <h4 key="additionalItemsHeader" className={styles.additionalItemsHeader}>
      {rm('Addonservice_Additional_Items_Header')}
    </h4>
  );
  selectedAddon.addonServices.forEach(childAddon => {
    res.push(
      ...showChildItems({
        addonService: childAddon,
        onClickCallback,
        validInstallations
      })
    );
  });

  return res;
};
